import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

import StockTicker from "@/components/StockTicker";
import Footer from "@/components/Footer";
import heroBg from "@/assets/hero-bg-optimized.webp";
import heroBgMobile from "@/assets/hero-bg-mobile.webp";
import logo from "@/assets/logo.webp";
import OpportunitiesSection from "@/components/OpportunitiesSection";
import AboutHero from "@/components/home/AboutHero";
import DivisionsGrid from "@/components/home/DivisionsGrid";
import InsightsPreview from "@/components/home/InsightsPreview";
// Homepage SEO tags (title, description, canonical, og:*, JSON-LD @graph) are
// served statically from index.html. No Helmet-driven SEO here — it would
// duplicate the canonical <link> and JSON-LD script on the homepage.

const Index = () => {
  return (
    <div className="homepage-faculty-headings min-h-dvh bg-navy-dark scroll-pt-header">
      {/* SEO handled statically in index.html for the homepage */}
      <main>
        {/* Hero — 60/40 split */}
        <section className="posture-split relative hero-shell flex flex-col lg:flex-row pt-[var(--header-h)] px-safe">
          {/* Left navy panel */}
          <div className="posture-pane lg:w-[60%] flex items-center bg-navy-dark px-6 md:px-12 lg:px-24 py-12 sm:py-16 md:py-20 lg:py-32 squat:py-10 short:py-8 text-paper">
            <div className="max-w-2xl animate-fade-up">
              <img
                src={logo}
                alt="JSA Investment Club logo"
                className="h-12 sm:h-14 lg:h-16 w-auto mb-6 sm:mb-8 lg:mb-10 [filter:brightness(0)_invert(1)] opacity-90"
                width={64}
                height={64}
              />
              <div className="flex items-center gap-4 mb-6 sm:mb-8">
                <div className="h-px w-12 bg-steel" />
                <span className="text-steel-light uppercase tracking-[0.4em] text-[10px] font-bold">
                  Est. 2012
                </span>
              </div>

              <h1 className="font-serif text-fluid-hero leading-[1.05] mb-6 sm:mb-8 lg:mb-10 text-paper">
                <span className="sr-only">Welcome to JSA Investment Club — Bridging theory and financial markets</span>
                <span aria-hidden="true">
                  Welcome <br />
                  <span className="italic font-normal text-steel-light">to</span> JSA Investment Club
                </span>
              </h1>

              <p className="text-fluid-lede text-paper/70 max-w-lg mb-8 sm:mb-10 lg:mb-12 leading-relaxed font-light">
                The premier student investment community at Jönköping
                International Business School — bridging classroom theory and
                the Nordic financial markets.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link
                  to="/apply"
                  className="inline-flex items-center gap-3 bg-steel text-paper px-8 lg:px-10 py-4 lg:py-5 font-bold text-[11px] uppercase tracking-[0.25em] hover:bg-paper hover:text-navy-dark transition-colors duration-300"
                >
                  Join JIC
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/portfolio"
                  className="inline-flex items-center gap-3 border border-white/20 text-paper px-8 lg:px-10 py-4 lg:py-5 font-bold text-[11px] uppercase tracking-[0.25em] hover:bg-white/10 transition-colors"
                >
                  Portfolio
                </Link>
              </div>
            </div>
          </div>

          {/* Right image panel */}
          <div className="lg:w-[40%] relative bg-navy min-h-[240px] sm:min-h-[320px] md:min-h-[400px] short:min-h-[160px] lg:min-h-0">
            <img
              src={heroBg}
              srcSet={`${heroBgMobile} 800w, ${heroBg} 1600w`}
              sizes="(max-width: 1023px) 100vw, 40vw"
              alt="Jönköping International Business School"
              className="absolute inset-0 w-full h-full object-cover grayscale contrast-125"
              width={1600}
              height={1200}
              fetchPriority="high"
              decoding="async"
            />

            <div className="absolute inset-0 bg-navy-dark/40 mix-blend-multiply" />
            <div className="absolute bottom-5 sm:bottom-8 left-5 sm:left-8 right-5 sm:right-8 flex justify-between items-end border-t border-white/10 pt-6">
              <div className="text-[9px] tracking-[0.25em] uppercase font-bold text-paper/60">
                Jönköping · Sweden
              </div>
              <div className="flex gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-steel-light" />
                <span className="w-1.5 h-1.5 rounded-full bg-paper/20" />
                <span className="w-1.5 h-1.5 rounded-full bg-paper/20" />
              </div>
            </div>
          </div>
        </section>

        {/* Mission + Stats (paper) */}
        <AboutHero />

        {/* Stock Ticker */}
        <StockTicker />

        {/* Our Committee — photo grid */}
        <DivisionsGrid />

        {/* Opportunities */}
        <OpportunitiesSection />

        {/* Latest from JIC */}
        <InsightsPreview />

        {/* CTA with oversized serif J */}
        <section className="relative py-14 sm:py-16 md:py-20 lg:py-32 squat:py-12 bg-navy text-center px-6 overflow-hidden">
          <div className="absolute inset-0 flex items-center justify-center opacity-[0.06] pointer-events-none select-none">
            <img
              src={logo}
              alt=""
              aria-hidden="true"
              className="w-[70vw] max-w-[680px] h-auto [filter:brightness(0)_invert(1)]"
            />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="font-serif text-fluid-h2 text-paper mb-6 sm:mb-8 leading-tight">
              Ready to invest in your future?
            </h2>
            <p className="text-paper/60 text-lg mb-12 max-w-xl mx-auto">
              Applications for the upcoming term are now being accepted. Secure
              your place in Jönköping&rsquo;s premier investment community.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/apply"
                className="inline-flex items-center justify-center gap-3 bg-paper text-navy-dark px-10 py-5 font-bold text-[11px] uppercase tracking-[0.25em] hover:bg-steel hover:text-paper transition-colors"
              >
                Submit Application
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/team"
                className="inline-flex items-center justify-center gap-3 border border-white/20 text-paper px-10 py-5 font-bold text-[11px] uppercase tracking-[0.25em] hover:bg-white/10 transition-colors"
              >
                Team
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Index;
